import { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import Button from '@mui/material/Button';

import { TFieldSchema, TFieldScope } from '../schemas/payment-provider-schema';

type SchemaFieldsListProps = {
  fields: TFieldSchema[];
  scope: TFieldScope;
  onAdd?: () => void;
  onEdit?: (field: TFieldSchema) => void;
  onDelete?: (field: TFieldSchema) => void;
  emptyText?: string;
  manageable?: boolean;
};

const formatLabel = (id: string) =>
  id.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const SchemaFieldsList = ({
  fields,
  scope,
  onAdd,
  onEdit,
  onDelete,
  emptyText,
  manageable = false,
}: SchemaFieldsListProps) => {
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  const scoped = fields
    .filter((f) => (f.scope ?? 'global') === scope)
    .sort((a, b) => (a.displayOrder ?? 999) - (b.displayOrder ?? 999));

  return (
    <Box sx={styles.container}>
      {manageable && (
        <Box sx={styles.toolbar}>
          <Typography sx={styles.scopeLabel}>
            {scope === 'global'
              ? 'Foodo-owned credentials shared across all restaurants.'
              : scope === 'client'
                ? 'Per-restaurant credentials. Set values inside each restaurant.'
                : 'Picked on-device from the acquirer terminal list. Not stored here.'}
          </Typography>
          {onAdd && (
            <Button
              size="small"
              startIcon={<AddIcon fontSize="small" />}
              onClick={onAdd}
              sx={styles.addBtn}
            >
              Add Field
            </Button>
          )}
        </Box>
      )}

      {scoped.length === 0 ? (
        <Box sx={styles.empty}>
          <Typography sx={styles.emptyText}>
            {emptyText || `No ${scope}-scope fields defined yet.`}
          </Typography>
        </Box>
      ) : (
        <Box sx={styles.list}>
          {scoped.map((field) => (
            <Box
              key={field.id}
              sx={styles.row}
              onMouseEnter={() => setHoveredId(field.id)}
              onMouseLeave={() => setHoveredId(null)}
            >
              <Box sx={styles.rowMain}>
                <Typography sx={styles.fieldLabel}>
                  {field.label || formatLabel(field.id)}
                </Typography>
                <Typography sx={styles.fieldId}>{field.id}</Typography>
              </Box>
              <Box sx={styles.rowMeta}>
                <Chip
                  size="small"
                  label={field.type}
                  sx={
                    field.type === 'secret'
                      ? { ...styles.chip, backgroundColor: '#fef3c7', color: '#92400e' }
                      : { ...styles.chip, backgroundColor: '#e0f2fe', color: '#075985' }
                  }
                />
                {field.required && (
                  <Chip
                    size="small"
                    label="required"
                    sx={{ ...styles.chip, backgroundColor: '#fee2e2', color: '#991b1b' }}
                  />
                )}
                <Typography sx={styles.orderText}>#{field.displayOrder ?? '-'}</Typography>
                {manageable && hoveredId === field.id && (
                  <Box sx={styles.actions}>
                    {onEdit && (
                      <Tooltip title="Edit label/required/order">
                        <IconButton size="small" onClick={() => onEdit(field)}>
                          <EditIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                    {onDelete && (
                      <Tooltip title="Delete field">
                        <IconButton
                          size="small"
                          onClick={() => onDelete(field)}
                          sx={{ color: '#dc2626' }}
                        >
                          <DeleteIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                )}
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default SchemaFieldsList;

const styles = {
  container: {
    width: '100%',
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 2,
    mb: 1.5,
  },
  scopeLabel: {
    fontFamily: 'GilroyMedium',
    fontSize: 12,
    color: '#64748b',
    flex: 1,
  },
  addBtn: {
    textTransform: 'capitalize' as const,
    color: '#0f172a',
    fontFamily: 'GilroySemiBold',
    fontSize: 12,
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    backgroundColor: '#ffffff',
    px: 1.5,
    '&:hover': { backgroundColor: '#f1f5f9' },
  },
  empty: {
    py: 3,
    textAlign: 'center' as const,
    border: '1px dashed #e2e8f0',
    borderRadius: '8px',
  },
  emptyText: {
    color: '#94a3b8',
    fontSize: 13,
    fontFamily: 'GilroyMedium',
  },
  list: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 0.5,
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 12px',
    borderRadius: '6px',
    border: '1px solid #e2e8f0',
    backgroundColor: '#ffffff',
    transition: 'background-color 0.15s ease',
    '&:hover': { backgroundColor: '#f8fafc' },
  },
  rowMain: {
    display: 'flex',
    flexDirection: 'column' as const,
  },
  fieldLabel: {
    fontFamily: 'GilroySemiBold',
    fontSize: 13,
    color: '#0f172a',
  },
  fieldId: {
    fontFamily: 'monospace',
    fontSize: 11,
    color: '#64748b',
  },
  rowMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: 0.75,
  },
  chip: {
    fontFamily: 'GilroySemiBold',
    fontSize: 10,
    height: 20,
  },
  orderText: {
    fontFamily: 'GilroyRegular',
    fontSize: 11,
    color: '#94a3b8',
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: 0.25,
    ml: 0.5,
  },
};
